import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { Search, Plus, Edit2, X, ChevronLeft, ChevronRight, Settings, Package, ZoomIn } from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const btnPrimary = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer', color: '#374151' };

function Field({ label, children, span }) {
  return (
    <div style={{ marginBottom: 14, gridColumn: span ? '1/-1' : undefined }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: wide ? 760 : 500, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Stock search for product linking ─────────────────────────────────────────
function StockLinker({ linkedStocks, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const search = useCallback(async (term) => {
    if (!term) { setResults([]); return; }
    try {
      const r = await api.get('/stock/items', { params: { q: term, per_page: 20, page: 1 } });
      setResults(r.data.items || []);
    } catch { }
  }, []);

  const handleInput = e => {
    setQ(e.target.value);
    setOpen(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(e.target.value), 300);
  };

  const addStock = stock => {
    if (linkedStocks.find(s => s.stock_id === stock.id)) {
      toast('Already added');
      return;
    }
    onChange([...linkedStocks, { stock_id: stock.id, item_code: stock.item_code, quantity: 1, name: stock.name_kh || stock.name_cn || stock.item_code, selling_price: parseFloat(stock.selling_price) || 0 }]);
    setQ('');
    setOpen(false);
  };

  const updateQty = (idx, qty) => {
    const updated = [...linkedStocks];
    updated[idx].quantity = parseInt(qty) || 1;
    onChange(updated);
  };

  const remove = idx => onChange(linkedStocks.filter((_, i) => i !== idx));

  return (
    <div>
      <div ref={wrapRef} style={{ position: 'relative', marginBottom: 8 }}>
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input value={q} onChange={handleInput} onFocus={() => setOpen(true)}
            placeholder="Search stock by code or name..."
            style={{ ...inputStyle, paddingLeft: 28 }} />
        </div>
        {open && results.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
            {results.map(s => (
              <div key={s.id} onMouseDown={() => addStock(s)}
                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 8, alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 6px', borderRadius: 4 }}>{s.item_code}</span>
                <span style={{ fontSize: 12, color: '#374151', flex: 1 }}>{s.name_kh || s.name_cn || ''}</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>Qty: {s.quantity}</span>
                {s.selling_price ? <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginLeft: 4 }}>${parseFloat(s.selling_price).toFixed(2)}</span> : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {linkedStocks.length > 0 && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          {linkedStocks.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: i < linkedStocks.length - 1 ? '1px solid #f3f4f6' : 'none', background: '#fafafa' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>{s.item_code}</span>
              <span style={{ fontSize: 12, color: '#374151', flex: 1 }}>{s.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: '#6b7280' }}>Qty:</span>
                <input type="number" min="1" value={s.quantity} onChange={e => updateQty(i, e.target.value)}
                  style={{ width: 50, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, textAlign: 'center' }} />
              </div>
              <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Add/Edit Product Modal ───────────────────────────────────────────────────
function ProductModal({ product, duplicateFrom, saleTypes, salePages, liveSession, onClose, onSaved }) {
  const isEdit = !!product;
  const isDuplicate = !!duplicateFrom;
  const src = duplicateFrom || {};

  const getDefaultDate = () => {
    if (liveSession?.session_date) return liveSession.session_date.split('T')[0];
    return new Date().toISOString().split('T')[0];
  };

  const [form, setForm] = useState({
    sale_type: product?.sale_type || src.sale_type || (liveSession?.page_id ? 'LS' : 'CS'),
    page_code: product?.page_code || src.page_code || liveSession?.page_code || (salePages[0]?.code || ''),
    session_code: product?.session_code || src.session_code || liveSession?.session_code || 'A',
    session_date: product?.session_date ? product.session_date.split('T')[0] : src.session_date ? src.session_date.split('T')[0] : getDefaultDate(),

    description: product?.description || src.description || '',
    stock_description: product?.stock_description || src.stock_description || '',
    selling_price: product?.selling_price ?? src.selling_price ?? '',
    original_price: product?.original_price || '',
    quantity: product?.quantity ?? src.quantity ?? 999,
    photo_url: product?.photo_url || src.photo_url || '',
    is_active: product?.is_active ?? 1,
  });
  const [linkedStocks, setLinkedStocks] = useState([]);
  const [nextSeq, setNextSeq] = useState(null);
  const [seqNo, setSeqNo] = useState(product?.sequence_no ? String(product.sequence_no).padStart(2,'0') : '');
  const [seqError, setSeqError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedType = saleTypes.find(t => t.code === form.sale_type);
  const hasSequence = selectedType?.has_sequence;

  const checkSeqDuplicate = useCallback(async (val) => {
    if (!hasSequence || !val || !form.sale_type || !form.page_code || !form.session_date) return;
    try {
      const r = await api.get('/products/check-sequence', { params: {
        sale_type: form.sale_type, page_code: form.page_code,
        session_code: form.session_code, session_date: form.session_date,
        sequence_no: val === '00' ? 0 : parseInt(val), exclude_id: product?.id || 0
      }});
      setSeqError(r.data.duplicate ? `#${String(val).padStart(2,'0')} already used in this ${form.sale_type === 'LS' ? 'live session' : 'date'}` : '');
    } catch {}
  }, [form.sale_type, form.page_code, form.session_code, form.session_date, hasSequence]);

  // Load existing stock links for edit or duplicate
  useEffect(() => {
    const stockSrc = isEdit ? product : isDuplicate ? duplicateFrom : null;
    if (stockSrc && stockSrc.stock_ids) {
      const ids = stockSrc.stock_ids.split(',');
      const codes = stockSrc.stock_codes ? stockSrc.stock_codes.split('|') : [];
      const qtys = stockSrc.stock_qtys ? stockSrc.stock_qtys.split(',') : [];
      setLinkedStocks(ids.map((id, i) => ({
        stock_id: parseInt(id), item_code: codes[i] || '',
        quantity: parseInt(qtys[i]) || 1, name: codes[i] || '',
        selling_price: 0
      })));
    }
  }, []);

  // OLD: Load existing stock links for edit
  useEffect(() => {
    if (false && isEdit && product.stock_ids) {
      const ids = product.stock_ids.split(',');
      const codes = product.stock_codes ? product.stock_codes.split('|') : [];
      const qtys = product.stock_qtys ? product.stock_qtys.split(',') : [];
      setLinkedStocks(ids.map((id, i) => ({
        stock_id: parseInt(id),
        item_code: codes[i] || '',
        quantity: parseInt(qtys[i]) || 1,
        name: codes[i] || ''
      })));
    }
  }, []);

  // Fetch next sequence
  useEffect(() => {
    if (!isEdit && hasSequence && form.sale_type && form.page_code && form.session_code && form.session_date) {
      api.get('/products/next-sequence', { params: { sale_type: form.sale_type, page_code: form.page_code, session_code: form.session_code, session_date: form.session_date } })
        .then(r => { setNextSeq(r.data.formatted); if (!isEdit && !seqNo) setSeqNo(r.data.formatted); }).catch(() => {});
    }
  }, [form.sale_type, form.page_code, form.session_code, form.session_date, hasSequence]);

  // Auto-build stock description and original_price from linked stocks
  useEffect(() => {
    if (linkedStocks.length > 0) {
      const parts = linkedStocks.map(s => {
        const code = s.item_code.replace(/\s+/g, '');
        return s.quantity > 1 ? `${code}x${s.quantity}` : code;
      });
      set('stock_description', parts.join('+'));
      // Auto-calculate original price
      const total = linkedStocks.reduce((sum, s) => sum + (parseFloat(s.selling_price) || 0) * (parseInt(s.quantity) || 1), 0);
      if (total > 0) set('original_price', total.toFixed(2));
    } else if (!isEdit) {
      set('original_price', '');
    }
  }, [linkedStocks]);

  // Build code preview
  const buildPreview = () => {
    const parts = [form.sale_type || '??', form.page_code || '??'];
    const dateStr = form.session_date ? new Date(form.session_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }).replace('/', '/') : '??/??';
    parts.push(`${form.session_code || 'A'}${dateStr}`);
    if (hasSequence) parts.push(isEdit ? String(product.sequence_no).padStart(2, '0') : (nextSeq || '??'));
    if (form.stock_description) parts.push(form.stock_description);
    return parts.join('-');
  };

  const submit = async () => {
    if (!form.sale_type || !form.page_code) return toast.error('Sale type and page required');
    setSaving(true);
    try {
      const seqVal = hasSequence ? (seqNo === '00' ? 0 : parseInt(seqNo)) : null;
      if (hasSequence && seqNo === '0') return toast.error('Use 00 instead of 0');
      const payload = { ...form, sequence_no: seqVal, stock_items: linkedStocks };
      if (seqError) return toast.error(seqError);
      if (isEdit) {
        await api.put(`/products/${product.id}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/products/', payload);
        toast.success('Product created');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? `Edit — ${product.product_code}` : 'Add Product'} onClose={onClose} wide>
      {/* Code preview */}
      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontFamily: 'monospace', color: '#0369a1', fontWeight: 700, marginBottom: 16 }}>
        Preview: {buildPreview()}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Sale Type *">
          <select value={form.sale_type} onChange={e => set('sale_type', e.target.value)} style={inputStyle}>
            {saleTypes.map(t => <option key={t.code} value={t.code}>{t.code} — {t.name}</option>)}
          </select>
        </Field>

        <Field label="Page *">
          <select value={form.page_code} onChange={e => set('page_code', e.target.value)} style={inputStyle}>
            {salePages.map(p => <option key={p.code} value={p.code}>{p.code} — {p.name}</option>)}
          </select>
        </Field>

        <Field label="Session Code">
          <input value={form.session_code} onChange={e => set('session_code', e.target.value.toUpperCase())} style={inputStyle} placeholder="A" />
        </Field>

        <Field label="Session Date">
          <input type="date" value={form.session_date} onChange={e => set('session_date', e.target.value)} style={inputStyle} />
        </Field>

        {!!hasSequence && (
          <Field label={`Sequence No ${form.sale_type === 'LS' ? '(unique per live session)' : '(unique per page+date)'}`}>
            <input type="text" value={seqNo}
              onChange={e => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setSeqNo(val);
                if (val === '0') { setSeqError('Use 00 instead of 0'); return; }
                if (val) checkSeqDuplicate(val);
              }}
              style={{ ...inputStyle, borderColor: seqError ? '#f87171' : '#d1d5db' }}
              placeholder="01" maxLength={2} />
            {seqError && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>{seqError}</div>}
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Auto-increments. Override with any number (00, 88, 99 for gifts)</div>
          </Field>
        )}

        <Field label="Selling Price (USD) *">
          <input type="number" step="0.01" value={form.selling_price} onChange={e => set('selling_price', e.target.value)} style={inputStyle} placeholder="0.00" />
        </Field>

        <Field label="Original Price (USD)">
          <input type="number" step="0.01" value={form.original_price} onChange={e => set('original_price', e.target.value)} style={inputStyle} placeholder="0.00" />
        </Field>

        <Field label="Available Qty">
          <input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)} style={inputStyle} />
        </Field>

        <Field label="Status">
          <select value={form.is_active} onChange={e => set('is_active', parseInt(e.target.value))} style={inputStyle}>
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </Field>

        <Field label="Stock Items" span>
          <StockLinker linkedStocks={linkedStocks} onChange={setLinkedStocks} />
        </Field>

        <Field label="Stock Description (auto-filled, editable)" span>
          <input value={form.stock_description} onChange={e => set('stock_description', e.target.value)} style={inputStyle} placeholder="e.g. BRPD01A000123+BRPD01A000456" />
        </Field>

        <Field label="Description" span>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
        </Field>

        <Field label="Photo URL" span>
          <input value={form.photo_url} onChange={e => set('photo_url', e.target.value)} style={inputStyle} placeholder="https://..." />
        </Field>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        <button onClick={onClose} style={btnGhost}>Cancel</button>
        <button onClick={submit} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : (isEdit ? 'Update' : 'Add Product')}</button>
      </div>
    </Modal>
  );
}

// ─── Settings Modal (sale types + pages) ─────────────────────────────────────
function SettingsModal({ onClose, onChanged }) {
  const [tab, setTab] = useState('types');
  const [types, setTypes] = useState([]);
  const [pages, setPages] = useState([]);
  const [typeForm, setTypeForm] = useState({ code: '', name: '', has_sequence: 0 });
  const [pageForm, setPageForm] = useState({ code: '', name: '' });
  const [editTypeId, setEditTypeId] = useState(null);
  const [editPageId, setEditPageId] = useState(null);

  const loadTypes = async () => { const r = await api.get('/products/sale-types'); setTypes(r.data); };
  const loadPages = async () => { const r = await api.get('/products/sale-pages'); setPages(r.data); };

  useEffect(() => { loadTypes(); loadPages(); }, []);

  const saveType = async () => {
    try {
      if (editTypeId) { await api.put(`/products/sale-types/${editTypeId}`, typeForm); }
      else { await api.post('/products/sale-types', typeForm); }
      toast.success('Saved'); setTypeForm({ code: '', name: '', has_sequence: 0 }); setEditTypeId(null); loadTypes(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const delType = async id => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/products/sale-types/${id}`); loadTypes(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const savePage = async () => {
    try {
      if (editPageId) { await api.put(`/products/sale-pages/${editPageId}`, pageForm); }
      else { await api.post('/products/sale-pages', pageForm); }
      toast.success('Saved'); setPageForm({ code: '', name: '' }); setEditPageId(null); loadPages(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const delPage = async id => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/products/sale-pages/${id}`); loadPages(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const tabStyle = active => ({ padding: '8px 16px', border: 'none', borderBottom: active ? '2px solid #2563eb' : '2px solid transparent', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400, color: active ? '#2563eb' : '#6b7280' });

  return (
    <Modal title="Product Settings" onClose={onClose} wide>
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
        <button style={tabStyle(tab === 'types')} onClick={() => setTab('types')}>Sale Types</button>
        <button style={tabStyle(tab === 'pages')} onClick={() => setTab('pages')}>Pages</button>
      </div>

      {tab === 'types' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 100px auto', gap: 8, marginBottom: 12, alignItems: 'end' }}>
            {[['Code', 'code', 'LS'], ['Name', 'name', 'Live Sale']].map(([label, key, ph]) => (
              <div key={key}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
                <input value={typeForm[key]} onChange={e => setTypeForm(f => ({ ...f, [key]: key === 'code' ? e.target.value.toUpperCase() : e.target.value }))}
                  style={inputStyle} placeholder={ph} disabled={key === 'code' && !!editTypeId} />
              </div>
            ))}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Has Sequence</div>
              <select value={typeForm.has_sequence} onChange={e => setTypeForm(f => ({ ...f, has_sequence: parseInt(e.target.value) }))} style={inputStyle}>
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={saveType} style={btnPrimary}>{editTypeId ? 'Update' : 'Add'}</button>
              {editTypeId && <button onClick={() => { setEditTypeId(null); setTypeForm({ code: '', name: '', has_sequence: 0 }); }} style={btnGhost}>✕</button>}
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              {['Code', 'Name', 'Sequence', ''].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>{t.code}</td>
                  <td style={{ padding: '8px 12px', color: '#111827' }}>{t.name}</td>
                  <td style={{ padding: '8px 12px' }}><span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 10, background: t.has_sequence ? '#dcfce7' : '#f3f4f6', color: t.has_sequence ? '#16a34a' : '#6b7280' }}>{t.has_sequence ? 'Yes' : 'No'}</span></td>
                  <td style={{ padding: '8px 12px', display: 'flex', gap: 6 }}>
                    <button onClick={() => { setEditTypeId(t.id); setTypeForm({ code: t.code, name: t.name, has_sequence: t.has_sequence }); }} style={btnGhost}><Edit2 size={12} /></button>
                    <button onClick={() => delType(t.id)} style={{ ...btnGhost, color: '#dc2626', borderColor: '#fca5a5' }}><X size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === 'pages' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: 8, marginBottom: 12, alignItems: 'end' }}>
            {[['Code', 'code', 'C1'], ['Name', 'name', '123 Crystal']].map(([label, key, ph]) => (
              <div key={key}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
                <input value={pageForm[key]} onChange={e => setPageForm(f => ({ ...f, [key]: key === 'code' ? e.target.value.toUpperCase() : e.target.value }))}
                  style={inputStyle} placeholder={ph} disabled={key === 'code' && !!editPageId} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={savePage} style={btnPrimary}>{editPageId ? 'Update' : 'Add'}</button>
              {editPageId && <button onClick={() => { setEditPageId(null); setPageForm({ code: '', name: '' }); }} style={btnGhost}>✕</button>}
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              {['Code', 'Name', ''].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {pages.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>{p.code}</td>
                  <td style={{ padding: '8px 12px', color: '#111827' }}>{p.name}</td>
                  <td style={{ padding: '8px 12px', display: 'flex', gap: 6 }}>
                    <button onClick={() => { setEditPageId(p.id); setPageForm({ code: p.code, name: p.name }); }} style={btnGhost}><Edit2 size={12} /></button>
                    <button onClick={() => delPage(p.id)} style={{ ...btnGhost, color: '#dc2626', borderColor: '#fca5a5' }}><X size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Modal>
  );
}

// ─── Photo cell ───────────────────────────────────────────────────────────────
function PhotoCell({ url }) {
  const [open, setOpen] = useState(false);
  if (!url) return <div style={{ width: 44, height: 44, background: '#f3f4f6', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Package size={16} color="#9ca3af" /></div>;
  return (
    <>
      <div onClick={() => setOpen(true)} style={{ width: 44, height: 44, borderRadius: 6, overflow: 'hidden', cursor: 'zoom-in', border: '1px solid #e5e7eb' }}>
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
      {open && <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={url} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
      </div>}
    </>
  );
}

// ─── Main Products Page ───────────────────────────────────────────────────────
export default function Products() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saleTypes, setSaleTypes] = useState([]);
  const [salePages, setSalePages] = useState([]);

  const [filterType, setFilterType] = useState('');
  const [filterPage, setFilterPage] = useState('');
  const [filterQ, setFilterQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState(null);

  const toggleActive = async item => {
    try {
      await api.put(`/products/${item.id}`, { ...item, is_active: item.is_active ? 0 : 1, stock_items: null });
      loadProducts();
    } catch (e) { toast.error('Failed to update status'); }
  };

  const duplicateProduct = item => {
    setDuplicateSource(item);
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { loadProducts(); }, [page, filterType, filterPage, filterStatus]);

  const loadMeta = async () => {
    const [t, p] = await Promise.all([api.get('/products/sale-types'), api.get('/products/sale-pages')]);
    setSaleTypes(t.data);
    setSalePages(p.data);
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 50 };
      if (filterType) params.sale_type = filterType;
      if (filterPage) params.page_code = filterPage;
      if (filterQ) params.q = filterQ;
      if (filterStatus !== '') params.is_active = filterStatus;
      const r = await api.get('/products/', { params });
      setItems(r.data.items);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  const TYPE_COLORS = { LS: '#dbeafe', CS: '#fef3c7', VS: '#ede9fe', RS: '#dcfce7', TS: '#fce7f3', FG: '#f3f4f6' };
  const TYPE_TEXT = { LS: '#1d4ed8', CS: '#92400e', VS: '#6d28d9', RS: '#166534', TS: '#9d174d', FG: '#374151' };

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Products</h1>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>{total.toLocaleString()} products</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowSettings(true)} style={{ ...btnGhost, display: 'flex', alignItems: 'center', gap: 6 }}><Settings size={14} /> Settings</button>
          <button onClick={() => setShowAdd(true)} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}><Plus size={14} /> Add Product</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 180px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>SEARCH</div>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input value={filterQ} onChange={e => setFilterQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && (setPage(1), loadProducts())} placeholder="Code, name..." style={{ ...inputStyle, paddingLeft: 28 }} />
          </div>
        </div>
        <div style={{ flex: '0 0 140px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>TYPE</div>
          <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="">All</option>
            {saleTypes.map(t => <option key={t.code} value={t.code}>{t.code} — {t.name}</option>)}
          </select>
        </div>
        <div style={{ flex: '0 0 150px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>PAGE</div>
          <select value={filterPage} onChange={e => { setFilterPage(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="">All</option>
            {salePages.map(p => <option key={p.code} value={p.code}>{p.code} — {p.name}</option>)}
          </select>
        </div>
        <div style={{ flex: '0 0 110px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>STATUS</div>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
        <button onClick={() => { setPage(1); loadProducts(); }} style={btnPrimary}>Search</button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {['Photo', 'Code', 'Product Name', 'Stock', 'Price', 'Qty', 'Sales Status', 'Status', 'Action'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: ['Price','Qty','Sales Status','Code'].includes(h) ? 'center' : 'left', fontWeight: 600, color: '#374151', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                <Package size={32} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.3 }} />
                No products yet
              </td></tr>
            )}
            {items.map(item => {
              const remaining = item.quantity - (item.total_ordered || 0);
              const isOutOfStock = remaining <= 0;
              const statusLabel = !item.is_active ? 'Inactive' : isOutOfStock ? 'Out of Stock' : 'Active';
              const statusColor = !item.is_active ? { bg: '#f3f4f6', text: '#9ca3af' } : isOutOfStock ? { bg: '#fee2e2', text: '#dc2626' } : { bg: '#dcfce7', text: '#16a34a' };
              return (
              <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                {/* Photo */}
                <td style={{ padding: '8px 12px' }}><PhotoCell url={item.photo_url} /></td>
                {/* # ID */}
                <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'monospace', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                  #{String(item.id).padStart(6, '0')}
                </td>
                {/* Product Name */}
                <td style={{ padding: '8px 12px', maxWidth: 260 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#1d4ed8', fontWeight: 600, wordBreak: 'break-all' }}>{item.product_code}</span>
                  {item.description && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{item.description.slice(0, 50)}{item.description.length > 50 ? '…' : ''}</div>}
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                    {item.created_by_name && <span>+ {item.created_by_name}</span>}
                    {item.updated_by_name && <span style={{ marginLeft: 6 }}>✎ {item.updated_by_name}</span>}
                  </div>
                </td>
                {/* Stock */}
                <td style={{ padding: '8px 12px', maxWidth: 160 }}>
                  {item.stock_codes
                    ? item.stock_codes.split('|').map((code, i) => (
                        <div key={i} style={{ fontFamily: 'monospace', fontSize: 10, background: '#f3f4f6', color: '#374151', padding: '1px 5px', borderRadius: 3, display: 'inline-block', marginRight: 3, marginBottom: 2 }}>{code}</div>
                      ))
                    : <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>}
                </td>
                {/* Price */}
                <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                  {item.selling_price != null ? `$${parseFloat(item.selling_price).toFixed(2)}` : '—'}
                </td>
                {/* Qty: remaining / total */}
                <td style={{ padding: '8px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  <span style={{ fontWeight: 600, color: isOutOfStock ? '#dc2626' : remaining <= 10 ? '#d97706' : '#111827' }}>{remaining}</span>
                  <span style={{ color: '#9ca3af', fontSize: 11 }}>/{item.quantity}</span>
                </td>
                {/* Sales Status */}
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, lineHeight: 1.8, whiteSpace: 'nowrap' }}>
                    <div><span style={{ color: '#374151', fontWeight: 600 }}>{item.total_ordered || 0}</span> <span style={{ color: '#9ca3af' }}>ordered</span></div>
                    <div><span style={{ color: '#d97706', fontWeight: 600 }}>{item.pending || 0}</span> <span style={{ color: '#9ca3af' }}>pending</span></div>
                    <div><span style={{ color: '#2563eb', fontWeight: 600 }}>{item.shipped || 0}</span> <span style={{ color: '#9ca3af' }}>shipped</span></div>
                    <div><span style={{ color: '#dc2626', fontWeight: 600 }}>{item.cancelled || 0}</span> <span style={{ color: '#9ca3af' }}>cancelled</span></div>
                  </div>
                </td>
                {/* Status */}
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: statusColor.bg, color: statusColor.text, whiteSpace: 'nowrap' }}>
                    {statusLabel}
                  </span>
                </td>
                {/* Action */}
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <button onClick={() => setEditProduct(item)} style={btnGhost} title="Edit"><Edit2 size={13} /></button>
                    <button onClick={() => duplicateProduct(item)} style={{ ...btnGhost, fontSize: 14 }} title="Duplicate" disabled={duplicating === item.id}>⧉</button>
                    <button onClick={() => toggleActive(item)} style={{ ...btnGhost, color: item.is_active ? '#dc2626' : '#16a34a', borderColor: item.is_active ? '#fca5a5' : '#86efac' }} title={item.is_active ? 'Disable' : 'Enable'}>
                      {item.is_active ? <X size={13} /> : <span style={{fontSize:13, lineHeight:1}}>✓</span>}
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 14, borderTop: '1px solid #e5e7eb' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={btnGhost}><ChevronLeft size={14} /></button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={btnGhost}><ChevronRight size={14} /></button>
          </div>
        )}
      </div>

      {showAdd && <ProductModal saleTypes={saleTypes} salePages={salePages} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadProducts(); }} />}
      {duplicateSource && <ProductModal saleTypes={saleTypes} salePages={salePages} duplicateFrom={duplicateSource} onClose={() => setDuplicateSource(null)} onSaved={() => { setDuplicateSource(null); loadProducts(); }} />}
      {editProduct && <ProductModal product={editProduct} saleTypes={saleTypes} salePages={salePages} onClose={() => setEditProduct(null)} onSaved={() => { setEditProduct(null); loadProducts(); }} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} onChanged={loadMeta} />}
    </div>
  );
}
